import { TaskQueue, UserType } from 'dy-frontend-http-repository/lib/data/enums';
import {
    TaskActivityUserResource,
    TaskStateTransitionResource,
} from 'dy-frontend-http-repository/lib/modules/Task/resources';
import { TaskSubTicketStatusLogResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { TextFormatUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { HTMLAttributes, useEffect, useRef } from 'react';

import Avatar from '@app/components/Avatar';
import Heading from '@app/components/Heading';
import TaskStateTag from '@app/containers/components/TaskStateTag';
import { $permissions } from '@app/containers/store/states';
import { Endpoints } from '@app/data/consts';
import { taskQueueInformation, taskSubTicketStatusInformation } from '@app/data/consts';
import { Colors, Icon, IconName, Intent, Spinner, Tag, Tooltip } from '@blueprintjs/core';
import Flex from '@components/Flex';
import RouterLink from '@components/RouterLink';
import DevText from '@components/Text';
import { imageHashPreview } from '@data/consts';
import { TaskQueueEffectiveState, TaskQueueStateTransitionResource } from '@pages/Task/data/types';
import { fetchSubTicketStatusLog } from '@pages/Task/pages/TaskSubTickets/store/effects';
import { resetSubTicketStatusLog } from '@pages/Task/pages/TaskSubTickets/store/events';
import { $subTicketStatusLog, selectSubTicketStatusLogRows } from '@pages/Task/pages/TaskSubTickets/store/states';
import {
    RequestTimelineEntry,
    getSubTicketStatusTagStyle,
    mergeRequestTimeline,
} from '@pages/Task/pages/TaskSubTickets/utils';
import { fetchTaskQueueStateTransitionLog } from '@pages/Task/store/effects';
import { resetTaskQueueStateTransitionLog } from '@pages/Task/store/events';
import { $task, $taskQueueStateTransitionLog, $taskStateTransitionLog } from '@pages/Task/store/states';

// Queue state vocabulary for the panel. Kept apart from `taskStateInformation` and the sub-task
// palette: these are outlined tags, so a queue change cannot be mistaken for either of the two
// filled vocabularies. `draft` is absent on purpose — the backend never emits a row touching it.
const taskQueueStateTagInformation: {
    [state in TaskQueueEffectiveState]: { title: string; icon: IconName; intent: Intent };
} = {
    [TaskQueue.ACTIVE]: { title: taskQueueInformation[TaskQueue.ACTIVE].title, icon: 'play', intent: Intent.SUCCESS },
    [TaskQueue.BACKLOG]: {
        title: taskQueueInformation[TaskQueue.BACKLOG].title,
        icon: 'remove-row-bottom',
        intent: Intent.NONE,
    },
    paused: { title: 'Paused', icon: 'pause', intent: Intent.WARNING },
};

export interface StateTransitionLogProps {
    isCollapsed: boolean;
}

export type Props = HTMLAttributes<HTMLDivElement> & StateTransitionLogProps;

const StateTransitionLog: React.FC<Props> = ({ isCollapsed, ...props }) => {
    const taskStateTransitionLog = useStore($taskStateTransitionLog);
    const permissions = useStore($permissions);
    const task = useStore($task);
    const subTicketStatusLogState = useStore($subTicketStatusLog);
    const taskQueueStateTransitionLog = useStore($taskQueueStateTransitionLog);

    const isSubTicketModuleEnabled = permissions.isEnabled.taskSubTicket || permissions.isRoot.taskSubTicket;

    // Exactly one status-log read per mounted Request. The header re-renders on every parent
    // store change, so without this guard a participant update would re-issue the same read.
    const requestedTaskIdRef = useRef<ID | null>(null);

    useEffect(() => {
        if (!task || !isSubTicketModuleEnabled) {
            return;
        }

        if (requestedTaskIdRef.current === task.id) {
            return;
        }

        requestedTaskIdRef.current = task.id;

        fetchSubTicketStatusLog({ filter: { task_id: task.id } }).catch((e) => {
            // The parent Request's own transitions are the panel's primary content and are
            // already on screen; a failed sub-task read degrades to showing just those
            console.error(e);
        });
    }, [task?.id, isSubTicketModuleEnabled]);

    useEffect(() => {
        return () => {
            requestedTaskIdRef.current = null;
            resetSubTicketStatusLog();
        };
    }, []);

    // Request queue/pause rows. Unlike the sub-task read this is NOT one-shot: it is re-read
    // after every in-page action that writes such a row — pause and resume flip `paused_at`,
    // archive and restore flip `archived_at` and the queue, and delivering writes a queue row
    // while only `state` changes in the store.
    useEffect(() => {
        if (!task) {
            return;
        }

        fetchTaskQueueStateTransitionLog(task.id).catch((e) => {
            // Same degradation as the sub-task read: the parent rows stay on screen
            console.error(e);
        });
    }, [task?.id, task?.state, task?.queue, task?.paused_at, task?.archived_at]);

    useEffect(() => {
        return () => {
            resetTaskQueueStateTransitionLog();
        };
    }, []);

    if (taskStateTransitionLog === null) {
        return (
            <Flex direction="row" justify="center">
                <Spinner />
            </Flex>
        );
    }

    // Sub-task rows the panel may also show, scoped to the Request they were read for
    const subTicketStatusLogRows = task ? selectSubTicketStatusLogRows(subTicketStatusLogState, task.id) : [];

    // One descending list. Parent entries are passed through untouched — this adds rows beside
    // them, it never rewrites one — so a Request with no phases renders exactly what it always
    // did.
    const timeline = mergeRequestTimeline({
        transitions: taskStateTransitionLog.items,
        statusLogs: subTicketStatusLogRows,
        queueTransitions: taskQueueStateTransitionLog ?? [],
    });

    // Queue rows are first-class history. A Request can therefore have visible queue/pause
    // history even when the legacy workflow-state list is empty.
    if (timeline.length === 0) {
        return null;
    }

    const renderDateHeader = (isFirstItem: boolean, shouldRender: boolean, at: string) => {
        if (!shouldRender) {
            return null;
        }

        const atMoment = moment(at);

        const renderDateSuffix = () => {
            const commonSuffixProps = { inline: true, muted: true, className: 'ml-2' };

            if (atMoment.isSame(moment(), 'day')) {
                return <DevText {...commonSuffixProps}>Today</DevText>;
            }

            return <DevText {...commonSuffixProps}>{TextFormatUtils.capitalize(atMoment.fromNow())}</DevText>;
        };

        return (
            <Heading className={['mb-1', isFirstItem ? '' : 'mt-3'].join(' ')} type="h5">
                {atMoment.format('D MMMM YYYY')}
                {renderDateSuffix()}
            </Heading>
        );
    };

    // Actor cell, shared by the Request workflow-state row and the Request queue row: both name
    // the same kind of person against the same resource, so they must never drift apart
    const renderActor = (user: TaskActivityUserResource | null) => {
        if (!user) {
            // Transition was initiated by platform not by user
            // TODO: add global config for bot information (name, image, etc.)
            return <DevText>System</DevText>;
        }

        // Create link to user page who initiated the transition
        let initiatorUserLink = Endpoints.CORE_USER_TASKS.replace(':coreUserId', `${user.id}`);
        if (user.type === UserType.CLIENT) {
            initiatorUserLink = Endpoints.CLIENT_TASKS.replace(':clientId', `${user.id}`);
        }

        // Get avatar src
        let avatarSrc: string | null = null;
        if (user.image_hash) {
            avatarSrc = imageHashPreview.userImage(user.image_hash, ImageHashPreviewSize.SM);
        }

        return (
            <>
                <Avatar
                    className="ml-small mr-small"
                    width="25px"
                    height="25px"
                    src={avatarSrc}
                    alt={user.first_name}
                />

                <RouterLink color={Colors.WHITE} to={initiatorUserLink} fontWeight={600}>
                    {user.first_name} {user.last_name}
                </RouterLink>
            </>
        );
    };

    const renderComment = (comment: string) => {
        if (!comment) {
            // There is no comment for this transition
            return null;
        }

        return (
            <Tooltip className="ml-1" content={<DevText>{comment}</DevText>}>
                <Icon style={{ cursor: 'pointer' }} color={Colors.GRAY2} icon="chat" />
            </Tooltip>
        );
    };

    const renderQueueStateTag = (state: TaskQueueEffectiveState, className?: string) => {
        const { title, icon, intent } = taskQueueStateTagInformation[state];

        return (
            <Tag minimal className={className} icon={icon} intent={intent}>
                {title}
            </Tag>
        );
    };

    // A Request queue/pause change: the same `from → to` shape as a sub-task row, in the queue
    // vocabulary (outlined tags, never `TaskStateTag`) — the Request's workflow state did not
    // move, its queue or its pause overlay did.
    const renderQueueListItem = (
        isFirstItem: boolean,
        shouldRenderDateHeader: boolean,
        transition: TaskQueueStateTransitionResource
    ) => (
        <>
            {renderDateHeader(isFirstItem, shouldRenderDateHeader, transition.applied_at)}

            <Flex direction="row" align="center" style={{ flexWrap: 'wrap' }}>
                <DevText inline muted className="mr-small">
                    {moment(transition.applied_at).format('HH:mm')}
                </DevText>

                {renderActor(transition.user)}

                <DevText inline className="ml-small mr-small">
                    moved request
                </DevText>

                {renderQueueStateTag(transition.from, 'mr-small')}

                <Icon className="mr-small" color={Colors.GRAY2} icon="arrow-right" />

                {renderQueueStateTag(transition.to)}

                {renderComment(transition.comment)}
            </Flex>
        </>
    );

    // A sub-task status transition. Deliberately labelled `Sub-task #N` and rendered with the
    // sub-task status tag rather than `TaskStateTag`: the parent Request did NOT move into this
    // state, one of its phases did, and the two vocabularies must never be shown as one.
    const renderSubTaskListItem = (
        isFirstItem: boolean,
        shouldRenderDateHeader: boolean,
        log: TaskSubTicketStatusLogResource
    ) => {
        const actorLink = Endpoints.CORE_USER_TASKS.replace(':coreUserId', `${log.actor_user.id}`);

        let avatarSrc: string | null = null;
        if (log.actor_user.image_hash) {
            avatarSrc = imageHashPreview.userImage(log.actor_user.image_hash, ImageHashPreviewSize.SM);
        }

        return (
            <>
                {renderDateHeader(isFirstItem, shouldRenderDateHeader, log.created_at)}

                <Flex direction="row" align="center" style={{ flexWrap: 'wrap' }}>
                    <DevText inline muted className="mr-small">
                        {moment(log.created_at).format('HH:mm')}
                    </DevText>

                    <Avatar
                        className="ml-small mr-small"
                        width="25px"
                        height="25px"
                        src={avatarSrc}
                        alt={log.actor_user.first_name}
                    />

                    <RouterLink color={Colors.WHITE} to={actorLink} fontWeight={600}>
                        {log.actor_user.first_name} {log.actor_user.last_name}
                    </RouterLink>

                    <DevText inline className="ml-small mr-small">
                        moved
                    </DevText>

                    <Tag minimal className="mr-small" icon="layers">
                        Sub-task #{log.ordinal}
                    </Tag>

                    <Tag className="mr-small" style={getSubTicketStatusTagStyle(log.from_status)}>
                        {taskSubTicketStatusInformation[log.from_status].title}
                    </Tag>

                    <Icon className="mr-small" color={Colors.GRAY2} icon="arrow-right" />

                    <Tag style={getSubTicketStatusTagStyle(log.to_status)}>
                        {taskSubTicketStatusInformation[log.to_status].title}
                    </Tag>

                    <DevText inline muted small className="ml-1">
                        {log.scope_summary}
                    </DevText>
                </Flex>
            </>
        );
    };

    const renderListItem = (
        isFirstItem: boolean,
        shouldRenderStateTransitionLogDateHeader: boolean,
        taskStateTransition: TaskStateTransitionResource
    ) => {
        const renderStateAppliedAtLabel = () => {
            return (
                <DevText inline muted className="mr-small">
                    {moment(taskStateTransition.applied_at).format('HH:mm')}
                </DevText>
            );
        };

        const renderStateTransitionUserInformation = () => renderActor(taskStateTransition.user);

        const renderStateTransitionComment = () => renderComment(taskStateTransition.comment);

        const renderStateTransitionDateHeader = () => {
            if (!shouldRenderStateTransitionLogDateHeader) {
                return null;
            }

            const stateTransitionAppliedAtMoment = moment(taskStateTransition.applied_at);

            const renderDateSuffix = () => {
                const commonSuffixProps = { inline: true, muted: true, className: 'ml-2' };
                const isStateTransitionDateToday = stateTransitionAppliedAtMoment.isSame(moment(), 'day');

                if (isStateTransitionDateToday) {
                    return <DevText {...commonSuffixProps}>Today</DevText>;
                }

                return (
                    <DevText {...commonSuffixProps}>
                        {TextFormatUtils.capitalize(stateTransitionAppliedAtMoment.fromNow())}
                    </DevText>
                );
            };

            return (
                <Heading className={['mb-1', isFirstItem ? '' : 'mt-3'].join(' ')} type="h5">
                    {stateTransitionAppliedAtMoment.format('D MMMM YYYY')}
                    {renderDateSuffix()}
                </Heading>
            );
        };

        return (
            <>
                {renderStateTransitionDateHeader()}

                <Flex direction="row" align="center">
                    {renderStateAppliedAtLabel()}
                    {renderStateTransitionUserInformation()}
                    <DevText inline className="ml-small mr-small">
                        changed status to
                    </DevText>
                    <TaskStateTag large={false} withCircle={false} state={taskStateTransition.state} />
                    {taskStateTransition.is_forced && (
                        <Tag minimal className="ml-1" icon="build" intent={Intent.DANGER}>
                            Forced
                        </Tag>
                    )}
                    {renderStateTransitionComment()}
                </Flex>
            </>
        );
    };

    const renderTaskStateTransitionLog = () => {
        // Unchanged rule, now evaluated over the merged list: a header opens each new calendar
        // day, whichever kind of entry happens to start it
        const shouldRenderDateHeader = (entry: RequestTimelineEntry, index: number) => {
            if (index === 0) {
                return true;
            }

            return !moment(timeline[index - 1].at).isSame(moment(entry.at), 'day');
        };

        return (
            <div>
                {timeline.map((entry, index) => (
                    <div
                        className="mb-1"
                        key={
                            entry.kind === 'queue' ? `queue-${entry.at}-${entry.transition.from}-${entry.transition.to}`
                            : entry.kind === 'request' ?
                                `request-${entry.at}-${entry.transition.state}`
                            :   `sub-task-${entry.log.id}`
                        }
                    >
                        {entry.kind === 'queue' ?
                            renderQueueListItem(index === 0, shouldRenderDateHeader(entry, index), entry.transition)
                        : entry.kind === 'request' ?
                            renderListItem(index === 0, shouldRenderDateHeader(entry, index), entry.transition)
                        :   renderSubTaskListItem(index === 0, shouldRenderDateHeader(entry, index), entry.log)}
                    </div>
                ))}
            </div>
        );
    };

    return <>{renderTaskStateTransitionLog()}</>;
};

export default StateTransitionLog;
